import { createReducer, createAction } from '@reduxjs/toolkit';

import { backendCall, createApiCall } from '@reducers/utils';
import { getDemoTools } from '@components/tours/helpers/demoDataRegistry';
import { Alert } from '@helpers';
import type { RootState, AppThunk } from '@store/types';
import type {
  ClampingToolsState,
  ClampingTool,
  GetToolsParams,
  NewToolsFoundParams,
  StoreToolRequestDto,
  DeleteClampingToolRequestDto,
} from './types';

// Re-export types for consumers
export type {
  ClampingToolsState,
  ClampingTool,
  GetToolsParams,
  NewToolsFoundParams,
  StoreClampingToolResponse,
  StoreToolResponse,
  StoreToolRequestDto,
  DeleteClampingToolRequestDto,
} from './types';

// === Initial State ===

const initState: ClampingToolsState = {
  clampingTools: [],
  isMore: true,
  loaded: false,
  pageNumber: 0,
  pageSize: 20,
  lastNCFileId: null,
};

// === Selectors ===

export const selectClampingTools = (state: RootState) => state.clampingTools.clampingTools;
export const selectClampingToolsLoaded = (state: RootState) => state.clampingTools.loaded;
export const selectLastNCFileId = (state: RootState) => state.clampingTools.lastNCFileId;

// === Actions ===

export const clear = createAction('clear');
export const close = createAction('clampingTools/close');
export const updateTools = createAction<ClampingTool[]>('clampingTools/updateTools');
export const setTools = createAction<ClampingTool[]>('clampingTools/setTools');
export const resetTools = createAction('clampingTools/resetTools');
export const setIsMore = createAction<boolean>('clampingTools/setIsMore');
export const updatePage = createAction<number | undefined>('clampingTools/updatePage');
export const updateLastNCFileId = createAction<number | null>('clampingTools/updateLastNCFileId');

// === Helper Functions ===

const filterUniqueTools = (tools: ClampingTool[] | undefined): ClampingTool[] => {
  if (!Array.isArray(tools)) return [];

  const uniqueToolsMap = new Map<number | undefined, ClampingTool>();
  tools.forEach((item) => {
    if (!uniqueToolsMap.has(item.toolId)) {
      uniqueToolsMap.set(item.toolId, item);
    }
  });
  return Array.from(uniqueToolsMap.values());
};

// === Thunks ===

let getToolsSignal: AbortController | null = null;

export const getTools =
  (params: GetToolsParams = {}): AppThunk<Promise<ClampingTool[] | undefined>> =>
  async (dispatch) => {
    const { ncFileId } = params;
    dispatch(updateLastNCFileId(ncFileId ?? null));

    // Demo data guard for tour
    const demoTools = getDemoTools(ncFileId);
    if (demoTools) {
      dispatch(setTools(demoTools as ClampingTool[]));
      return demoTools as ClampingTool[];
    }

    if (getToolsSignal) {
      getToolsSignal.abort();
    }

    getToolsSignal = new AbortController();
    const signal = getToolsSignal.signal;

    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.get, {
          params: {
            filter: {
              NcFileId: {
                operator: 'eq',
                value: ncFileId,
              },
            },
          },
          signal,
          onSuccess: (data) => {
            dispatch(setTools(filterUniqueTools(data)));
          },
          onError: (error) => {
            console.log(error, 'error');
            dispatch(setTools([]));
          },
        })
      )
    );

    return filterUniqueTools(result);
  };

export const getToolsForManyFiles =
  (ids: string): AppThunk<Promise<ClampingTool[] | undefined>> =>
  async (dispatch) => {
    const result = await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.get, {
          params: {
            filter: {
              NcFileId: {
                operator: 'in',
                value: `(${ids})`,
              },
            },
          },
        })
      )
    );

    return filterUniqueTools(result);
  };

export const getToolById =
  (toolId: number | string): AppThunk<Promise<ClampingTool[] | undefined>> =>
  async (dispatch) => {
    if (getToolsSignal) {
      getToolsSignal.abort();
    }

    getToolsSignal = new AbortController();
    const signal = getToolsSignal.signal;

    return await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.get, {
          params: {
            filter: {
              ToolId: {
                operator: 'eq',
                value: toolId,
              },
            },
          },
          signal,
          onSuccess: (result) => {
            dispatch(setTools(result));
          },
          onError: (error) => {
            console.log(error, 'error');
            dispatch(setTools([]));
          },
        })
      )
    );
  };

export const onFoundNewClampingTools =
  (e: NewToolsFoundParams): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (e.ncFileId) {
      dispatch(newToolsFound(e));
    }
  };

export const newToolsFound =
  ({ ncFileId }: NewToolsFoundParams): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      clampingTools: { lastNCFileId },
    } = getState();

    if (lastNCFileId !== ncFileId) {
      return;
    }
    await dispatch(getTools({ ncFileId }));
  };

export const create =
  (data: StoreToolRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.storeTool, {
          params: data,
          onSuccess: () => {},
          onError: (error) => {
            console.log(error, 'error');
            const errorMessage =
              typeof error === 'object' ? error?.responseException?.exceptionMessage : error;
            Alert({ title: errorMessage || 'Error', type: 'danger' });
          },
        })
      )
    );
  };

export const edit =
  (data: StoreToolRequestDto): AppThunk<Promise<void>> =>
  async (dispatch) => {
    await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.storeTool, {
          params: data,
          onSuccess: () => {},
          onError: (error) => {
            console.log(error, 'error');
            const errorMessage =
              typeof error === 'object' ? error?.responseException?.exceptionMessage : error;
            Alert({ title: errorMessage || 'Error', type: 'danger' });
          },
        })
      )
    );
  };

export const remove =
  (data: DeleteClampingToolRequestDto): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    const {
      clampingTools: { clampingTools },
    } = getState();

    await dispatch(
      backendCall(
        createApiCall((api) => api.clampingTools.remove, {
          params: data,
          onSuccess: async () => {
            const newTools = clampingTools.filter((t) => t.clampingToolId !== data.clampingToolId);
            await dispatch(setTools(newTools));
          },
          onError: (error) => {
            console.log(error, 'error');
            const errorMessage =
              typeof error === 'object' ? error?.responseException?.exceptionMessage : error;
            Alert({ title: errorMessage || 'Error', type: 'danger' });
          },
        })
      )
    );
  };

// === Reducer ===

export default createReducer<ClampingToolsState>(initState, (builder) => {
  builder
    .addCase(clear, () => initState)
    .addCase(close, () => initState)
    .addCase(updatePage, (state, action) => ({
      ...state,
      pageNumber: action.payload ? action.payload : state.pageNumber + 1,
    }))
    .addCase(setIsMore, (state, action) => ({
      ...state,
      isMore: action.payload,
    }))
    .addCase(setTools, (state, action) => ({
      ...state,
      clampingTools: action.payload,
      loaded: true,
    }))
    .addCase(resetTools, (state) => ({
      ...state,
      clampingTools: [],
      loaded: false,
    }))
    .addCase(updateLastNCFileId, (state, action) => ({
      ...state,
      lastNCFileId: action.payload,
    }));
});
